import { apiFetch } from "../lib/api";
import type { BarOut } from "./bars";

export type DeploymentStatus =
  | "CREATED"
  | "STARTING"
  | "RUNNING"
  | "STOPPING"
  | "STOPPED"
  | "ERROR"
  | "KILLED";

export interface InstrumentRef {
  symbol: string;
  exchange: string;
  instrument_type: string;
  expiry?: string | null;
  strike?: string | null;
  lot_size?: number;
  tick_size?: string;
}

export interface RiskRails {
  daily_loss_cap_pct?: string | null;
  daily_loss_cap_rupees?: string | null;
  max_position_lots_per_instrument?: number | null;
  max_open_orders?: number | null;
  kill_switch_engaged?: boolean;
}

export interface Deployment {
  id: string;
  user_id: string;
  name: string;
  strategy_key: string;
  mode: "paper" | "live";
  status: DeploymentStatus;
  last_error: string | null;
  capital: string;
  margin: string;
  timeframe: string;
  params: Record<string, unknown>;
  instruments: InstrumentRef[];
  broker_map: Record<string, string>;
  sizing_mode: string;
  sizing_params: Record<string, unknown>;
  risk_rails: RiskRails;
  notify_telegram: boolean;
  created_at: string;
  updated_at: string;
  /** Computed server-side, never stored. True when an admin revoked the
   *  strategy grant or broker access this deployment needs. A RUNNING
   *  deployment keeps running — only Start is refused. */
  policy_blocked?: boolean;
  policy_reason?: string | null;
}

export interface DeploymentCreate {
  name: string;
  strategy_key: string;
  mode: "paper" | "live";
  capital: string;
  margin?: string;
  timeframe: string;
  params: Record<string, unknown>;
  instruments: InstrumentRef[];
  broker_map: Record<string, string>;
  sizing_mode: string;
  sizing_params: Record<string, unknown>;
  risk_rails: RiskRails;
  notify_telegram?: boolean;
}

export interface DeploymentPositionView {
  instrument_key: string;
  symbol: string;
  side: "LONG" | "SHORT" | "FLAT";
  quantity: string;
  avg_price: string;
  current_price: string | null;
  market_value: string | null;
  realized_pnl: string;
  unrealized_pnl: string;
  /** Underlying (deployment-symbol) lots-weighted avg entry; null when flat. */
  deployment_avg: number | null;
  /** Freshest underlying (deployment-symbol) price; null before first tick. */
  deployment_current: number | null;
}

export interface DeploymentSummary {
  deployment_id: string;
  status: string;
  ticks_processed: number;
  bars_processed: number;
  orders_submitted: number;
  orders_rejected: number;
  /** Accepted by the broker but confirmed not to have filled as asked. */
  orders_unfilled: number;
  /** Submitted and not yet accounted for — normally 0. */
  orders_awaiting_fill: number;
  /** Set when an order's outcome is bad or unknown; both need the same action. */
  fill_alert: string | null;
  /** False when these came from the database because the runner is gone
   *  (stopped/killed) rather than from a live runner. The panel used to blank
   *  entirely in that case; render them as "last known", not as current. */
  live: boolean;
  realized_pnl: string;
  /** Realized P&L for the whole IST day, including earlier runner sessions of
   *  the same day (restored from the durable day state). `realized_pnl` above
   *  is session-scoped, so it resets to 0 on every restart; this one doesn't. */
  day_realized_pnl: string;
  positions: DeploymentPositionView[];
  last_prices: Record<string, string>;
  total_unrealized_pnl: string;
  total_invested: string;
  /** What the RUNNER is anchored to right now — its own golden number, armed
   *  side and ladder. Distinct from GET /golden-numbers, which recomputes the
   *  level from the store and can silently disagree (both September 2026
   *  incidents were exactly that). null when the runner is gone or the
   *  strategy has no golden concept. */
  golden_state?: {
    golden: string | null;
    /** anchored: trading off it · provisional: the (H+L+C)/3 fallback, to be
     *  replaced by the opening-window level · ready: today's level is known,
     *  the strategy anchors on it when its golden candle closes · pending:
     *  the opening window has not completed yet. */
    golden_status?: "anchored" | "provisional" | "ready" | "pending" | null;
    direction: string | null;
    first_candle_done: boolean;
    ladder: { level: string; lots: number; filled: boolean }[];
  } | null;
  /** Age of the most recent tick in seconds; null before the first tick. */
  seconds_since_last_tick: number | null;
  /** Set by the staleness watchdog while the feed is silent; null when healthy. */
  feed_alert: string | null;
  /** Deployment-symbol P&L in underlying points (Golden Number). realized =
   *  closed legs today; overall = realized + open-position unrealized. Null for
   *  strategies that don't expose it. */
  realized_points_pnl: number | null;
  overall_points_pnl: number | null;
}

export interface ReconnectFeedResult {
  ok: boolean;
  rebuilt?: number;
  /** Why it did not reconnect (e.g. "Zerodha rejected the access token"). */
  reason?: string | null;
  errors?: string[];
}

export async function listDeployments(): Promise<Deployment[]> {
  return apiFetch<Deployment[]>("/deployments");
}

export async function getDeployment(id: string): Promise<Deployment> {
  return apiFetch<Deployment>(`/deployments/${id}`);
}

export async function createDeployment(payload: DeploymentCreate): Promise<Deployment> {
  return apiFetch<Deployment>("/deployments", { method: "POST", body: payload });
}

/** Config editable on a *stopped* deployment (strategy + mode are fixed at
 *  create). Rejected with 409 while the deployment is running. */
export interface DeploymentUpdate {
  name?: string;
  capital?: string;
  margin?: string;
  timeframe?: string;
  params?: Record<string, unknown>;
  instruments?: InstrumentRef[];
  broker_map?: Record<string, string>;
  sizing_mode?: string;
  sizing_params?: Record<string, unknown>;
  risk_rails?: RiskRails;
  notify_telegram?: boolean;
}

export async function updateDeployment(
  id: string,
  payload: DeploymentUpdate,
): Promise<Deployment> {
  return apiFetch<Deployment>(`/deployments/${id}`, { method: "PATCH", body: payload });
}

/** Copy a deployment's config into a NEW one, optionally in the other mode.
 *
 *  This is how a mode changes — `DeploymentUpdate` has no `mode` field on
 *  purpose. A deployment accumulates history (day state, trade log, carried
 *  position) that carries no mode of its own, so flipping it in place would
 *  rewrite paper history as live, and could offer a simulated position for
 *  adoption as a real one. A clone inherits config and nothing else.
 *
 *  Safe to call on a running deployment — the source is untouched. */
export interface DeploymentCloneRequest {
  name?: string;
  /** Omit to keep the source's mode (a straight duplicate). */
  mode?: "paper" | "live";
}

export async function cloneDeployment(
  id: string,
  payload: DeploymentCloneRequest = {},
): Promise<Deployment> {
  return apiFetch<Deployment>(`/deployments/${id}/clone`, {
    method: "POST",
    body: payload,
  });
}

export async function deleteDeployment(id: string): Promise<void> {
  await apiFetch<void>(`/deployments/${id}`, { method: "DELETE" });
}

/** What to do about a position a previous Stop left open with the broker.
 *  Omit when the deployment is flat; the server refuses to guess when it isn't. */
export type OpenPositionChoice = "adopt" | "flat";

export async function startDeployment(
  id: string,
  openPosition?: OpenPositionChoice,
): Promise<Deployment> {
  return apiFetch<Deployment>(`/deployments/${id}/start`, {
    method: "POST",
    ...(openPosition ? { body: { open_position: openPosition } } : {}),
  });
}

export async function stopDeployment(id: string): Promise<Deployment> {
  return apiFetch<Deployment>(`/deployments/${id}/stop`, { method: "POST" });
}

export async function killDeployment(id: string): Promise<Deployment> {
  return apiFetch<Deployment>(`/deployments/${id}/kill`, { method: "POST" });
}

export async function reconnectFeed(id: string): Promise<ReconnectFeedResult> {
  return apiFetch<ReconnectFeedResult>(`/deployments/${id}/reconnect-feed`, {
    method: "POST",
  });
}

export async function setDeploymentNotifications(
  id: string,
  enabled: boolean,
): Promise<Deployment> {
  return apiFetch<Deployment>(`/deployments/${id}/notifications`, {
    method: "POST",
    body: { enabled },
  });
}

export async function getDeploymentSummary(id: string): Promise<DeploymentSummary> {
  return apiFetch<DeploymentSummary>(`/deployments/${id}/summary`);
}

// ---------------------------------------------------------------------------
// Durable per-trading-day state. Unlike DeploymentSummary this survives a stop,
// a kill and a backend restart — it is what the max-loss start block reads.
// ---------------------------------------------------------------------------

export type DayStopReason =
  | "max_loss"
  | "profit"
  | "max_flips"
  | "kill_switch"
  | "manual_kill"
  | "risk_cap";

export interface DayState {
  deployment_id: string;
  /** IST trading date this row accounts for (YYYY-MM-DD). */
  trade_date: string;
  realized_rupees: string;
  realized_points: number | null;
  max_loss_hit: boolean;
  /** Why the day stopped; null when it has not, or on rows from before
   *  2026-11-02 (the page then falls back to the P&L sign). */
  stop_reason: DayStopReason | null;
  reset_at: string | null;
  reset_count: number;
  /** True when Start will be refused until the user resets the day. */
  blocks_start: boolean;
}

export async function getDeploymentDayState(id: string): Promise<DayState> {
  return apiFetch<DayState>(`/deployments/${id}/day-state`);
}

/** Clear today's remembered P&L, points and max-loss flag. Order/fill history
 *  is left untouched; only the day's accounting restarts. 409s while running. */
export async function resetDeploymentDay(id: string): Promise<DayState> {
  return apiFetch<DayState>(`/deployments/${id}/reset-day`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// A position a previous Stop left open with the broker. Stop deliberately
// abandons rather than closes, so the next Start has to ask what to do with it.
// ---------------------------------------------------------------------------

export interface AbandonedPosition {
  deployment_id: string;
  symbol: string;
  instrument_type: string;
  expiry: string | null;
  strike: string | null;
  /** Underlying direction ("bull"/"bear"), not the option type. */
  direction: string;
  quantity: string;
  lots: number;
  avg_price: string;
  /** IST date the position was opened on. */
  trade_date: string;
  as_of: string;
  days_ago: number;
  /** The contract's expiry has passed — adopting it is almost certainly wrong. */
  expired: boolean;
  /** Live only: what the broker reports holding now. Null when unavailable. */
  broker_quantity: string | null;
  /** Our record disagrees with the broker — usually a hand-closed position. */
  broker_mismatch: boolean;
}

/** The carried position, or null when the deployment is flat. */
export async function getAbandonedPosition(
  id: string,
): Promise<AbandonedPosition | null> {
  return apiFetch<AbandonedPosition | null>(
    `/deployments/${id}/abandoned-position`,
  );
}

// ---------------------------------------------------------------------------
// Per-deployment trade log (orders + fills, paginated, most-recent first)
// ---------------------------------------------------------------------------

export interface OrderEvent {
  id: string;
  deployment_id: string;
  client_order_id: string;
  broker_order_id: string | null;
  instrument_key: string;
  symbol: string;
  side: "BUY" | "SELL";
  order_type: string;
  product: string;
  quantity_lots: number;
  limit_price: string | null;
  trigger_price: string | null;
  tag: string | null;
  rejected: boolean;
  rejection_reason: string | null;
  submitted_at: string;
}

export interface FillEvent {
  id: string;
  deployment_id: string;
  client_order_id: string;
  instrument_key: string;
  symbol: string;
  side: "BUY" | "SELL";
  quantity: string;
  price: string;
  fees: string;
  realized_pnl_after: string;
  filled_at: string;
}

export async function listDeploymentOrders(
  id: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<OrderEvent[]> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return apiFetch<OrderEvent[]>(`/deployments/${id}/orders${qs ? "?" + qs : ""}`);
}

export async function listDeploymentFills(
  id: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<FillEvent[]> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return apiFetch<FillEvent[]>(`/deployments/${id}/fills${qs ? "?" + qs : ""}`);
}

export interface GoldenNumber {
  day: string; // IST date "YYYY-MM-DD"
  value: string;
}

export async function getDeploymentGoldenNumbers(
  id: string,
  start: string,
  end: string,
): Promise<GoldenNumber[]> {
  const params = new URLSearchParams({ start, end });
  return apiFetch<GoldenNumber[]>(
    `/deployments/${id}/golden-numbers?${params.toString()}`,
  );
}

/** Session candles (recent completed + the forming bar) for the deployment's
 *  base symbol, aggregated live from the tick stream. Empty when not running.
 *  Poll fast and feed to the chart's `liveBars` to update on every tick. */
export async function getDeploymentLiveBars(id: string): Promise<BarOut[]> {
  return apiFetch<BarOut[]>(`/deployments/${id}/live-bars`);
}
